/**
 * SRES · Phase E — Final Review step
 * ----------------------------------
 * Renders every AI-generated / manually-authored narrative paragraph
 * for the current project, lets a Competent Person or Qualified
 * Person **approve** it with a digital signature or **reject** it
 * with a mandatory reason. Both actions hit existing backend
 * endpoints (`POST /narratives/{id}/approve|reject`) — no schema
 * change in this pass.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  CheckCircle, XCircle, Signature, Lock, Warning, DownloadSimple,
  Play, ArrowClockwise, CircleNotch, Stack, Link as LinkIcon, Copy,
} from "@phosphor-icons/react";

import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import SignaturePad from "@/components/SignaturePad";
import NarrativeJobsPanel, { JOBS_QUERY_KEY } from "@/pages/wizard/NarrativeJobsPanel";
import useProjectJobsSocket from "@/hooks/useProjectJobsSocket";

const STATUS_LABEL = {
  draft:                     "Draft",
  imported:                  "Imported",
  ai_generated:              "AI-generated",
  manually_edited:           "Manually edited",
  validated:                 "Validated",
  technical_review_required: "Technical review required",
  technically_approved:      "Technically approved",
  cp_qp_review_required:     "CP/QP review required",
  cp_qp_approved:            "CP/QP APPROVED",
  rejected:                  "Rejected",
  superseded:                "Superseded",
  locked:                    "Locked",
};

const STATUS_TONE = {
  cp_qp_approved: "text-emerald-700 bg-emerald-50 border-emerald-200",
  rejected:       "text-rose-700   bg-rose-50   border-rose-200",
  ai_generated:   "text-slate-700  bg-slate-50  border-slate-200",
  draft:          "text-amber-700  bg-amber-50  border-amber-200",
  locked:         "text-slate-700  bg-slate-100 border-slate-300",
};

// ═══════════════════════════════════════════════════════════════════
export default function FinalReviewStep() {
  const { pid } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const qc = useQueryClient();
  const [chapterFilter, setChapterFilter] = useState("");
  const [statusFilter,  setStatusFilter]  = useState("");
  const [approveTarget, setApproveTarget] = useState(null);
  const [rejectTarget,  setRejectTarget]  = useState(null);
  const [bulkTarget,    setBulkTarget]    = useState(null);
  const [showJobs,      setShowJobs]      = useState(false);
  const deepLinkParagraphId = searchParams.get("paragraph");
  const deepLinkHandledRef  = useRef(null);

  const narrativesQ = useQuery({
    queryKey: ["final-review-narratives", pid, chapterFilter, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (chapterFilter) params.set("chapter", chapterFilter);
      if (statusFilter)  params.set("review_status", statusFilter);
      params.set("limit", "500");
      const { data } = await api.get(`/projects/${pid}/narratives?${params}`);
      return data.items || [];
    },
    enabled: !!pid,
  });

  const statusQ = useQuery({
    queryKey: ["final-review-status", pid],
    queryFn:  () => api.get(`/projects/${pid}/narratives/status`).then((r) => r.data),
    enabled:  !!pid,
    refetchInterval: 30_000,
  });

  const signaturesQ = useQuery({
    queryKey: ["final-review-signatures", pid],
    queryFn:  () => api.get(`/projects/${pid}/narrative-signatures`).then((r) => r.data),
    enabled:  !!pid,
    refetchInterval: 30_000,
  });

  const matrixQ = useQuery({
    queryKey: ["final-review-approval-matrix", pid],
    queryFn:  () => api.get(`/projects/${pid}/approval-matrix`).then((r) => r.data),
    enabled:  !!pid,
    staleTime: 60_000,
  });

  // ── P0 · Generate-all jobs (restart-recovery UI) ──────────────────
  // Uses the SHARED cache key so mounting the same panel on Stage 12
  // stays in sync with this one automatically (iter76 · Feb 2026). This
  // local query only exists to power the header dot-badge — the actual
  // panel body lives in `NarrativeJobsPanel`.
  const jobsQ = useQuery({
    queryKey: JOBS_QUERY_KEY(pid),
    queryFn:  () => api.get(`/projects/${pid}/generate-all-jobs?limit=10`).then((r) => r.data),
    enabled:  !!pid,
    refetchInterval: 8_000,
  });

  // ── Iter77 · Feb 2026 · Real-time WS push ────────────────────────
  // A single hook subscribes to every job_* / paragraph_* /
  // bulk_approve_done event for the project. In addition to the shared
  // JOBS cache the hook also invalidates the FR-specific queries so the
  // paragraph list, status counters, and signatures table refresh
  // instantly when a co-signer approves in another tab.
  useProjectJobsSocket(pid, {
    invalidateCacheKeys: [
      ["final-review-narratives", pid, chapterFilter, statusFilter],
      ["final-review-status", pid],
      ["final-review-signatures", pid],
    ],
  });

  const items = useMemo(() => narrativesQ.data || [], [narrativesQ.data]);
  const chapters = useMemo(() => {
    const s = new Set(items.map((p) => p.chapter).filter(Boolean));
    return Array.from(s).sort();
  }, [items]);

  // ── P2 · Deep-link handler (Iter77 · Feb 2026) ───────────────────
  // If the URL includes ?paragraph=<paragraph_id>, scroll that
  // paragraph card into view and auto-open the Approve dialog.
  // Runs once per paragraph id — the ref ensures repeated re-renders
  // (e.g. after real-time WS invalidations) don't re-trigger it.
  useEffect(() => {
    if (!deepLinkParagraphId) return;
    if (deepLinkHandledRef.current === deepLinkParagraphId) return;
    if (!items.length) return;
    const target = items.find(
      (p) => (p.paragraph_id || p.id) === deepLinkParagraphId
    );
    if (!target) return;
    deepLinkHandledRef.current = deepLinkParagraphId;
    // Give the DOM a tick to paint the paragraph card before scrolling.
    setTimeout(() => {
      const el = document.querySelector(
        `[data-testid="fr-paragraph-${deepLinkParagraphId}"]`
      );
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-2", "ring-brand-blue", "ring-offset-2");
        setTimeout(() => {
          el.classList.remove("ring-2", "ring-brand-blue", "ring-offset-2");
        }, 3500);
      }
      // Auto-open approve dialog unless the paragraph is terminal.
      const isTerminal = ["cp_qp_approved", "rejected", "superseded"]
        .includes(target.review_status) || target.locked;
      if (!isTerminal) {
        setApproveTarget(target);
      } else {
        toast.info("Paragraph is already " + (target.review_status || "closed") + ".");
      }
    }, 120);
  }, [deepLinkParagraphId, items]);

  const clearDeepLink = () => {
    if (!deepLinkParagraphId) return;
    const next = new URLSearchParams(searchParams);
    next.delete("paragraph");
    setSearchParams(next, { replace: true });
  };

  const approveMut = useMutation({
    mutationFn: ({ id, body }) =>
      api.post(`/projects/${pid}/narratives/${id}/approve`, body).then((r) => r.data),
    onSuccess: (resp) => {
      const missing = resp?.missing_roles || [];
      if (missing.length > 0) {
        toast.success(
          `Signed as ${resp.role.toUpperCase()}. Awaiting ${missing.join("/").toUpperCase()} co-signature to fully approve.`
        );
      } else {
        toast.success("Paragraph approved and signed.");
      }
      qc.invalidateQueries({ queryKey: ["final-review-narratives", pid] });
      qc.invalidateQueries({ queryKey: ["final-review-status", pid] });
      qc.invalidateQueries({ queryKey: ["final-review-signatures", pid] });
      setApproveTarget(null);
    },
    onError: (e) => {
      const msg = e?.response?.data?.detail?.message || e?.response?.data?.detail || e.message;
      toast.error(`Approval failed: ${msg}`);
    },
  });

  const rejectMut = useMutation({
    mutationFn: ({ id, body }) =>
      api.post(`/projects/${pid}/narratives/${id}/reject`, body).then((r) => r.data),
    onSuccess: () => {
      toast.success("Paragraph rejected.");
      qc.invalidateQueries({ queryKey: ["final-review-narratives", pid] });
      qc.invalidateQueries({ queryKey: ["final-review-status", pid] });
      setRejectTarget(null);
    },
    onError: (e) => {
      const msg = e?.response?.data?.detail?.message || e?.response?.data?.detail || e.message;
      toast.error(`Rejection failed: ${msg}`);
    },
  });

  // ── P1 · Bulk-approve every paragraph in a chapter ────────────────
  const bulkMut = useMutation({
    mutationFn: ({ chapter, body }) =>
      api.post(`/projects/${pid}/chapters/${chapter}/bulk-approve`, body)
              .then((r) => r.data),
    onSuccess: (resp) => {
      const parts = [
        `${resp.approved_count} approved`,
        resp.partial_count > 0 ? `${resp.partial_count} awaiting co-signature` : null,
        resp.skipped > 0 ? `${resp.skipped} skipped` : null,
        resp.failed_count > 0 ? `${resp.failed_count} failed` : null,
      ].filter(Boolean);
      toast.success(`Chapter “${resp.chapter}” — ${parts.join(" · ")}.`);
      qc.invalidateQueries({ queryKey: ["final-review-narratives", pid] });
      qc.invalidateQueries({ queryKey: ["final-review-status", pid] });
      qc.invalidateQueries({ queryKey: ["final-review-signatures", pid] });
      setBulkTarget(null);
    },
    onError: (e) => {
      const msg = e?.response?.data?.detail?.message || e?.response?.data?.detail || e.message;
      toast.error(`Bulk approval failed: ${msg}`);
    },
  });

  // ── Presentational sub-cards ──────────────────────────────────────
  const totals = statusQ.data || {};
  const approvedCount = totals?.by_status?.cp_qp_approved || 0;
  const rejectedCount = totals?.by_status?.rejected || 0;
  const totalParas    = totals?.total_paragraphs || 0;

  return (
    <div className="h-full min-h-0 flex flex-col bg-slate-50" data-testid="final-review-step">
      {/* Header + counts */}
      <header className="shrink-0 bg-white border-b border-slate-200 px-6 py-3">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="font-heading font-black text-2xl tracking-tight text-slate-900"
                  data-testid="fr-title">
            Final Review
          </h1>
          <div className="text-[11px] text-slate-500">
            CP / QP sign-off · paragraph statuses · digital signatures
          </div>
          {matrixQ.data && (
            <span
              className="ml-2 text-[10.5px] uppercase tracking-widest px-2 py-0.5 rounded border border-slate-200 bg-slate-50 text-slate-600"
              data-testid="fr-matrix-badge"
              title={`Required signer roles per ${matrixQ.data.reporting_standard}`}
            >
              {matrixQ.data.reporting_standard.replace(/_/g, " ")} ·{" "}
              {matrixQ.data.required_roles.map((r) => r.toUpperCase()).join(" + ")}
              {matrixQ.data.dual_signoff && " · Dual sign-off"}
            </span>
          )}
          <div className="flex-1" />
          <MetricPill label="Approved"  value={approvedCount} tone="emerald" testId="fr-metric-approved" />
          <MetricPill label="Rejected"  value={rejectedCount} tone="rose"    testId="fr-metric-rejected" />
          <MetricPill label="Total"     value={totalParas}    tone="slate"   testId="fr-metric-total" />
          <MetricPill label="Signatures" value={signaturesQ.data?.count || 0}
                            tone="brand" testId="fr-metric-signatures" />
          <button
            type="button"
            onClick={() => setShowJobs((v) => !v)}
            data-testid="fr-jobs-toggle"
            className="inline-flex items-center gap-1 text-[11px] text-brand-blue hover:underline"
          >
            <Stack size={12} weight="bold" /> Generation jobs
            {(jobsQ.data?.items || []).some((j) => j.status === "interrupted") && (
              <span className="ml-1 inline-block w-2 h-2 rounded-full bg-amber-500"
                        title="Interrupted jobs available to resume" />
            )}
          </button>
          <button
            type="button"
            onClick={async () => {
              try {
                const { data } = await api.get(
                  `/projects/${pid}/narrative-signatures.csv`,
                  { responseType: "blob" },
                );
                const url = URL.createObjectURL(data);
                const a   = document.createElement("a");
                a.href = url;
                a.download = `signatures-${pid.slice(0, 8)}.csv`;
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(url);
                toast.success("Audit CSV downloaded.");
              } catch (e) {
                toast.error("CSV export failed.");
              }
            }}
            data-testid="fr-export-csv"
            className="inline-flex items-center gap-1 text-[11px] text-brand-blue hover:underline"
          >
            <DownloadSimple size={12} weight="bold" /> Audit CSV
          </button>
        </div>
      </header>

      {/* Filters */}
      <div className="shrink-0 bg-white border-b border-slate-200 px-6 py-2 flex gap-3 items-center flex-wrap">
        <div className="text-[10.5px] uppercase tracking-widest text-slate-500 mr-2">
          Filter
        </div>
        <select
          value={chapterFilter}
          onChange={(e) => setChapterFilter(e.target.value)}
          data-testid="fr-filter-chapter"
          className="h-8 text-[12px] border border-slate-200 rounded-md px-2 bg-white"
        >
          <option value="">All chapters</option>
          {chapters.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="fr-filter-status"
          className="h-8 text-[12px] border border-slate-200 rounded-md px-2 bg-white"
        >
          <option value="">All statuses</option>
          {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <div className="text-[11px] text-slate-500">
          {items.length} paragraph{items.length === 1 ? "" : "s"} shown
        </div>
      </div>

      {/* P0 · Jobs panel (collapsible) — resume interrupted generate-all runs.
                       Now backed by the reusable NarrativeJobsPanel so Stage 12
                       shares the same cache. */}
      {showJobs && (
        <div className="shrink-0 border-b border-slate-200 bg-slate-50 px-6 py-3"
                data-testid="fr-jobs-panel">
          <NarrativeJobsPanel
            projectId={pid}
            title="Full-project generation jobs"
            showStartButton={true}
            showOnlyMissingToggle={false}
            testIdPrefix="fr"
          />
        </div>
      )}

      {/* Body — paragraph list */}
      <main className="flex-1 min-h-0 overflow-y-auto px-6 py-4" data-testid="fr-list">
        {narrativesQ.isLoading && (
          <div className="text-slate-500 text-sm">Loading narrative paragraphs…</div>
        )}
        {narrativesQ.isSuccess && items.length === 0 && (
          <div className="rounded-lg border border-dashed border-slate-300 bg-white p-6 text-slate-500 text-sm">
            No narrative paragraphs match the current filter. Run
            <span className="mx-1 font-mono">POST /narratives/generate-all</span>
            from Stage 12 to draft the 27 chapters.
          </div>
        )}
        {/* Group paragraphs by chapter so we can offer a bulk-approve action per group. */}
        <div className="space-y-4">
          {groupByChapter(items).map((grp) => {
            const pendingInChapter = grp.items.filter(
              (p) => !["cp_qp_approved", "rejected", "superseded"].includes(p.review_status)
                        && !p.locked
            ).length;
            return (
              <section key={grp.chapter} data-testid={`fr-chapter-${grp.chapter}`}>
                <header className="flex items-center gap-2 mb-2">
                  <div className="text-[10.5px] uppercase tracking-widest text-slate-500">
                    Chapter · {grp.chapter}
                  </div>
                  <div className="text-[10.5px] text-slate-400">
                    ({grp.items.length} paragraph{grp.items.length === 1 ? "" : "s"} · {pendingInChapter} pending)
                  </div>
                  <div className="flex-1" />
                  {pendingInChapter > 0 && (
                    <Button
                      size="sm"
                      onClick={() => setBulkTarget({ chapter: grp.chapter, pending: pendingInChapter })}
                      data-testid={`fr-bulk-approve-${grp.chapter}`}
                      className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white gap-1"
                    >
                      <Signature size={11} weight="fill" />
                      Sign entire chapter ({pendingInChapter})
                    </Button>
                  )}
                </header>
                <div className="space-y-3">
                  {grp.items.map((p) => (
                    <ParagraphCard key={p.paragraph_id || p.id}
                      paragraph={p}
                      onApprove={() => setApproveTarget(p)}
                      onReject={() => setRejectTarget(p)} />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      </main>

      {/* Bulk-approve dialog */}
      <BulkApproveDialog
        open={!!bulkTarget}
        target={bulkTarget}
        matrix={matrixQ.data}
        onClose={() => setBulkTarget(null)}
        onSubmit={(body) => bulkMut.mutate({
          chapter: bulkTarget.chapter, body,
        })}
        submitting={bulkMut.isPending}
      />

      {/* Approve dialog */}
      <ApproveDialog
        open={!!approveTarget}
        target={approveTarget}
        matrix={matrixQ.data}
        onClose={() => { setApproveTarget(null); clearDeepLink(); }}
        onSubmit={(body) => approveMut.mutate({
          id: approveTarget.paragraph_id || approveTarget.id, body,
        })}
        submitting={approveMut.isPending}
      />

      {/* Reject dialog */}
      <RejectDialog
        open={!!rejectTarget}
        target={rejectTarget}
        onClose={() => { setRejectTarget(null); clearDeepLink(); }}
        onSubmit={(body) => rejectMut.mutate({
          id: rejectTarget.paragraph_id || rejectTarget.id, body,
        })}
        submitting={rejectMut.isPending}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
function MetricPill({ label, value, tone, testId }) {
  const bg = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rose:    "bg-rose-50    text-rose-700    border-rose-200",
    slate:   "bg-slate-50   text-slate-700   border-slate-200",
    brand:   "bg-brand-blue/10 text-brand-blue border-brand-blue/20",
  }[tone] || "bg-slate-50 text-slate-700 border-slate-200";
  return (
    <div
      className={`inline-flex items-baseline gap-1.5 rounded-md border px-2.5 py-1 ${bg}`}
      data-testid={testId}
    >
      <span className="text-[10px] uppercase tracking-widest">{label}</span>
      <span className="font-mono font-semibold text-[13px]">{value}</span>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
function ParagraphCard({ paragraph, onApprove, onReject }) {
  const status = paragraph.review_status || "draft";
  const label  = STATUS_LABEL[status] || status;
  const tone   = STATUS_TONE[status]  || "text-slate-600 bg-slate-50 border-slate-200";
  const isApproved = status === "cp_qp_approved";
  const isRejected = status === "rejected";
  const locked = !!paragraph.locked;

  return (
    <article
      className="rounded-lg border border-slate-200 bg-white overflow-hidden"
      data-testid={`fr-paragraph-${paragraph.paragraph_id || paragraph.id}`}
    >
      <header className="flex items-center gap-2 px-4 py-2 border-b border-slate-100 flex-wrap">
        <div className="text-[11px] uppercase tracking-widest text-slate-500">
          {paragraph.chapter || "—"}
          {paragraph.section && ` · ${paragraph.section}`}
        </div>
        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${tone}`}>
          {label}
        </span>
        {locked && (
          <span className="text-[10px] flex items-center gap-1 text-slate-500">
            <Lock size={11} /> Locked
          </span>
        )}
        <div className="flex-1" />
        {paragraph.approved_by && (
          <div className="text-[10.5px] text-emerald-700 flex items-center gap-1">
            <Signature size={12} weight="fill" /> Signed by {paragraph.approved_by}
          </div>
        )}
      </header>
      <div className="px-4 py-3 text-[13px] text-slate-800 leading-relaxed whitespace-pre-wrap">
        {paragraph.text || <span className="text-slate-400 italic">(empty paragraph)</span>}
      </div>
      <footer className="flex items-center gap-2 px-4 py-2 border-t border-slate-100 bg-slate-50">
        <div className="text-[10.5px] text-slate-500 font-mono">
          v{paragraph.version || 1} · {paragraph.word_count || 0} words
        </div>
        <button
          type="button"
          onClick={() => {
            const pid = paragraph.paragraph_id || paragraph.id;
            const url = `${window.location.origin}${window.location.pathname}?paragraph=${pid}`;
            navigator.clipboard?.writeText(url).then(
              () => toast.success("Deep-link copied — paste into any browser or PDF."),
              () => toast.error("Clipboard blocked — long-press to copy manually."),
            );
          }}
          data-testid={`fr-copy-deeplink-${paragraph.paragraph_id || paragraph.id}`}
          className="inline-flex items-center gap-1 text-[10.5px] text-slate-500 hover:text-brand-blue"
          title="Copy a shareable deep-link that auto-opens the approve dialog for this paragraph"
        >
          <LinkIcon size={11} weight="bold" /> Copy link
        </button>
        <div className="flex-1" />
        <Button
          type="button" size="sm" variant="outline"
          onClick={onReject}
          disabled={isRejected || isApproved || locked}
          data-testid="fr-reject-btn"
          className="text-[11.5px] gap-1 text-rose-600 hover:bg-rose-50 hover:text-rose-700 border-rose-200 disabled:opacity-40"
        >
          <XCircle size={12} weight="fill" /> Reject
        </Button>
        <Button
          type="button" size="sm"
          onClick={onApprove}
          disabled={isApproved || isRejected || locked}
          data-testid="fr-approve-btn"
          className="text-[11.5px] gap-1 bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40"
        >
          <CheckCircle size={12} weight="fill" /> Approve & Sign
        </Button>
      </footer>
    </article>
  );
}

// ═══════════════════════════════════════════════════════════════════
function ApproveDialog({ open, target, matrix, onClose, onSubmit, submitting }) {
  const requiredRoles = matrix?.required_roles || ["cp"];
  const dualSignoff   = requiredRoles.length > 1;
  const defaultRole   = requiredRoles[0] || "cp";
  const [role,      setRole]      = useState(defaultRole);
  const [mode,      setMode]      = useState("draw");   // "draw" | "type"
  const [typed,     setTyped]     = useState("");
  const [drawn,     setDrawn]     = useState("");        // data-URL
  const [notes,     setNotes]     = useState("");
  const [pin,       setPin]       = useState("");
  const padRef = useRef(null);

  // Reset local form each time the dialog re-opens.
  const targetId = target?.paragraph_id || target?.id;
  useEffect(() => {
    if (open) {
      setRole(defaultRole);
      setMode("draw");
      setTyped("");
      setDrawn("");
      setNotes("");
      setPin("");
      padRef.current?.clear?.();
    }
  }, [open, targetId, defaultRole]);

  const signaturePayload = mode === "draw" ? drawn : typed.trim();
  const canSubmit = signaturePayload && signaturePayload.length >= 4 && !submitting;

  // Already-signed roles for the current paragraph (from API response).
  const alreadySigned = new Set(target?.signed_roles || []);

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit({
      role,
      signature: signaturePayload,
      notes: notes.trim() || undefined,
      pin:   pin.trim()   || undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="fr-approve-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Signature size={18} weight="fill" className="text-emerald-600" />
            Approve & Sign
          </DialogTitle>
          <DialogDescription className="text-[11.5px] text-slate-500">
            Sign this paragraph with a CP/QP signature. The signature is SHA-256
            hashed and time-stamped in the immutable audit trail.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {dualSignoff && (
            <div className="rounded border border-amber-200 bg-amber-50 text-amber-800 text-[11.5px] px-3 py-2"
                    data-testid="fr-approve-dual-notice">
              This paragraph requires <strong>{requiredRoles.map((r) => r.toUpperCase()).join(" + ")}</strong>{" "}
              signatures under the project&apos;s reporting standard. It stays open
              until both signers approve.
              {alreadySigned.size > 0 && (
                <div className="mt-1 text-[10.5px] text-amber-700">
                  Already signed by: {Array.from(alreadySigned).map((r) => r.toUpperCase()).join(", ")}
                </div>
              )}
            </div>
          )}

          <div className="text-[12px] text-slate-600 border border-slate-200 rounded p-3 bg-slate-50">
            <div className="text-[10.5px] uppercase tracking-widest text-slate-500 mb-1">
              Paragraph
            </div>
            {(target?.text || "").slice(0, 240)}{(target?.text || "").length > 240 && "…"}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="text-[10.5px] uppercase tracking-widest text-slate-500 mb-1">Signing as</div>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                data-testid="fr-approve-role"
                className="w-full h-9 text-[12.5px] border border-slate-200 rounded px-2 bg-white"
              >
                {["cp", "qp"].map((r) => (
                  <option key={r} value={r}
                              disabled={dualSignoff && alreadySigned.has(r)}>
                    {r === "cp" ? "Competent Person (CP)" : "Qualified Person (QP)"}
                    {dualSignoff && alreadySigned.has(r) ? " · signed" : ""}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <div className="text-[10.5px] uppercase tracking-widest text-slate-500 mb-1">
                Confirmation PIN (optional)
              </div>
              <Input value={pin} onChange={(e) => setPin(e.target.value)}
                        placeholder="4-digit PIN"
                        data-testid="fr-approve-pin" />
            </label>
          </div>

          {/* Signature capture — canvas by default, typed fallback */}
          <div>
            <div className="flex items-center gap-3 mb-1.5">
              <div className="text-[10.5px] uppercase tracking-widest text-slate-500">
                Signature <span className="text-rose-500">*</span>
              </div>
              <div className="flex-1" />
              <div className="inline-flex rounded border border-slate-200 overflow-hidden text-[10.5px]"
                      data-testid="fr-approve-mode-switch">
                <button type="button"
                            onClick={() => setMode("draw")}
                            data-testid="fr-approve-mode-draw"
                            className={`px-2 py-1 ${mode === "draw" ? "bg-brand-blue text-white" : "bg-white text-slate-600"}`}>
                  Draw
                </button>
                <button type="button"
                            onClick={() => setMode("type")}
                            data-testid="fr-approve-mode-type"
                            className={`px-2 py-1 ${mode === "type" ? "bg-brand-blue text-white" : "bg-white text-slate-600"}`}>
                  Type
                </button>
              </div>
            </div>
            {mode === "draw" ? (
              <SignaturePad ref={padRef} onChange={setDrawn} testId="fr-approve-pad" />
            ) : (
              <Input value={typed}
                        onChange={(e) => setTyped(e.target.value)}
                        placeholder="Type your full name, title & registration"
                        data-testid="fr-approve-signature"
                        className="font-serif italic text-[15px]" />
            )}
            <div className="mt-1 text-[10.5px] text-slate-400">
              Signatures are SHA-256 hashed and time-stamped immutably.
            </div>
          </div>

          <label className="block">
            <div className="text-[10.5px] uppercase tracking-widest text-slate-500 mb-1">
              Review notes (optional)
            </div>
            <Textarea value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          rows={3}
                          placeholder="Brief note on why this paragraph is fit for CPR publication…"
                          data-testid="fr-approve-notes" />
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="fr-approve-cancel">
            Cancel
          </Button>
          <Button
            disabled={!canSubmit}
            onClick={handleSubmit}
            data-testid="fr-approve-submit"
            className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1"
          >
            <CheckCircle size={12} weight="fill" />
            {submitting ? "Signing…" : "Sign & Approve"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════════════
function RejectDialog({ open, target, onClose, onSubmit, submitting }) {
  const [reason, setReason] = useState("");
  const [notes,  setNotes]  = useState("");
  const canSubmit = reason.trim().length >= 3 && !submitting;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="fr-reject-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Warning size={18} weight="fill" className="text-rose-600" />
            Reject paragraph
          </DialogTitle>
          <DialogDescription className="text-[11.5px] text-slate-500">
            Rejected paragraphs are moved out of the CP/QP queue with the reason
            recorded in the review log.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="text-[12px] text-slate-600 border border-slate-200 rounded p-3 bg-slate-50">
            {(target?.text || "").slice(0, 240)}{(target?.text || "").length > 240 && "…"}
          </div>
          <label className="block">
            <div className="text-[10.5px] uppercase tracking-widest text-slate-500 mb-1">
              Reason <span className="text-rose-500">*</span>
            </div>
            <Input value={reason} onChange={(e) => setReason(e.target.value)}
                       placeholder="Concise reason for rejection"
                       data-testid="fr-reject-reason" />
          </label>
          <label className="block">
            <div className="text-[10.5px] uppercase tracking-widest text-slate-500 mb-1">
              Notes (optional)
            </div>
            <Textarea value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          rows={3}
                          data-testid="fr-reject-notes" />
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="fr-reject-cancel">
            Cancel
          </Button>
          <Button
            disabled={!canSubmit}
            onClick={() => onSubmit({ reason: reason.trim(),
                                          notes:  notes.trim() || undefined })}
            data-testid="fr-reject-submit"
            className="bg-rose-600 hover:bg-rose-700 text-white gap-1"
          >
            <XCircle size={12} weight="fill" />
            {submitting ? "Rejecting…" : "Reject"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════════════
// P1 · Bulk-approve dialog — signs every non-terminal paragraph in a
// chapter with the same signature. Uses the same signature-pad
// component as the single-paragraph approve dialog.
// ═══════════════════════════════════════════════════════════════════
function BulkApproveDialog({ open, target, matrix, onClose, onSubmit, submitting }) {
  const requiredRoles = matrix?.required_roles || ["cp"];
  const defaultRole   = requiredRoles[0] || "cp";
  const [role,      setRole]      = useState(defaultRole);
  const [mode,      setMode]      = useState("draw");
  const [typed,     setTyped]     = useState("");
  const [drawn,     setDrawn]     = useState("");
  const [notes,     setNotes]     = useState("");
  const [pin,       setPin]       = useState("");
  const padRef = useRef(null);

  useEffect(() => {
    if (open) {
      setRole(defaultRole);
      setMode("draw");
      setTyped(""); setDrawn("");
      setNotes(""); setPin("");
      padRef.current?.clear?.();
    }
  }, [open, target?.chapter, defaultRole]);

  const signaturePayload = mode === "draw" ? drawn : typed.trim();
  const canSubmit = signaturePayload && signaturePayload.length >= 4 && !submitting;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="fr-bulk-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Signature size={18} weight="fill" className="text-emerald-600" />
            Sign entire chapter
          </DialogTitle>
          <DialogDescription className="text-[11.5px] text-slate-500">
            One signature is stamped on every non-terminal paragraph in the
            chapter. Already-approved or rejected paragraphs are skipped
            automatically.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded border border-emerald-200 bg-emerald-50 text-emerald-800 text-[12px] px-3 py-2">
            <span className="font-semibold">{target?.pending ?? 0}</span> pending
            paragraph{target?.pending === 1 ? "" : "s"} in chapter{" "}
            <span className="font-mono font-semibold">{target?.chapter}</span> will be
            signed with the same signature.
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="text-[10.5px] uppercase tracking-widest text-slate-500 mb-1">Signing as</div>
              <select value={role}
                          onChange={(e) => setRole(e.target.value)}
                          data-testid="fr-bulk-role"
                          className="w-full h-9 text-[12.5px] border border-slate-200 rounded px-2 bg-white">
                <option value="cp">Competent Person (CP)</option>
                <option value="qp">Qualified Person (QP)</option>
              </select>
            </label>
            <label className="block">
              <div className="text-[10.5px] uppercase tracking-widest text-slate-500 mb-1">
                PIN (optional)
              </div>
              <Input value={pin} onChange={(e) => setPin(e.target.value)}
                        placeholder="4-digit PIN" data-testid="fr-bulk-pin" />
            </label>
          </div>

          <div>
            <div className="flex items-center gap-3 mb-1.5">
              <div className="text-[10.5px] uppercase tracking-widest text-slate-500">
                Signature <span className="text-rose-500">*</span>
              </div>
              <div className="flex-1" />
              <div className="inline-flex rounded border border-slate-200 overflow-hidden text-[10.5px]"
                      data-testid="fr-bulk-mode-switch">
                <button type="button" onClick={() => setMode("draw")}
                            data-testid="fr-bulk-mode-draw"
                            className={`px-2 py-1 ${mode === "draw" ? "bg-brand-blue text-white" : "bg-white text-slate-600"}`}>
                  Draw
                </button>
                <button type="button" onClick={() => setMode("type")}
                            data-testid="fr-bulk-mode-type"
                            className={`px-2 py-1 ${mode === "type" ? "bg-brand-blue text-white" : "bg-white text-slate-600"}`}>
                  Type
                </button>
              </div>
            </div>
            {mode === "draw" ? (
              <SignaturePad ref={padRef} onChange={setDrawn} testId="fr-bulk-pad" />
            ) : (
              <Input value={typed}
                        onChange={(e) => setTyped(e.target.value)}
                        placeholder="Type full name, title & registration"
                        data-testid="fr-bulk-signature"
                        className="font-serif italic text-[15px]" />
            )}
          </div>

          <label className="block">
            <div className="text-[10.5px] uppercase tracking-widest text-slate-500 mb-1">
              Review notes (optional — attached to every signature)
            </div>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                          rows={3}
                          placeholder="Chapter-level review note…"
                          data-testid="fr-bulk-notes" />
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="fr-bulk-cancel">
            Cancel
          </Button>
          <Button
            disabled={!canSubmit}
            onClick={() => onSubmit({
              role, signature: signaturePayload,
              notes: notes.trim() || undefined,
              pin:   pin.trim()   || undefined,
            })}
            data-testid="fr-bulk-submit"
            className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1"
          >
            <CheckCircle size={12} weight="fill" />
            {submitting ? "Signing chapter…" : `Sign ${target?.pending ?? 0} paragraph${target?.pending === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Helper — group items by chapter, preserving the API order.
// ═══════════════════════════════════════════════════════════════════
function groupByChapter(items) {
  const map = new Map();
  for (const p of items || []) {
    const key = p.chapter || "(no chapter)";
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(p);
  }
  return Array.from(map.entries()).map(([chapter, items]) => ({ chapter, items }));
}
